import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Inbox, ShieldCheck, PenLine, Package, Wrench, Plus, Trash2, Lock, MapPin } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Container, PlanBadge, ShoreBadge, GoldButton, OutlineButton, EmptyState } from "@/components/shared";
import { PLANS } from "@/data/plans";

function ProductManager({ prod, reload }) {
  const { products, limit } = prod;
  const [form, setForm] = useState({ name: "", type: "product", price: "", description: "", image_url: "" });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const atLimit = limit !== -1 && products.length >= limit;
  const noPaid = limit === 0;

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/my/products", form);
      setForm({ name: "", type: "product", price: "", description: "", image_url: "" });
      setOpen(false);
      await reload();
      toast.success("Product listed.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/my/products/${id}`);
    await reload();
    toast.success("Removed.");
  };

  return (
    <div className="mt-12" data-testid="product-manager">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-serif text-2xl text-[#1E1E2F] flex items-center gap-2"><Package size={20} className="text-[#CDA434]" /> Products & services</h2>
        <span className="font-sans text-xs text-[#505A68]">{products.length}{limit === -1 ? "" : ` / ${limit}`} listed</span>
      </div>

      {noPaid ? (
        <div className="bg-[#CDA434]/10 border border-[#CDA434]/50 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="product-upgrade-prompt">
          <p className="font-sans text-sm text-[#1B2430]">
            <Lock size={14} className="inline mr-1.5 -mt-0.5 text-[#7a611c]" />
            The free Harbour plan lists your company only. Upgrade to publish products and services on the Trade floor.
          </p>
          <a href="#plan" onClick={(e) => { e.preventDefault(); document.getElementById("plan-panel")?.scrollIntoView({ behavior: "smooth" }); }} className="font-sans text-sm font-semibold text-[#003366] whitespace-nowrap link-underline">Choose a plan →</a>
        </div>
      ) : (
        <>
          {products.length > 0 && (
            <div className="space-y-3 mb-4">
              {products.map((p) => (
                <div key={p.id} data-testid={`my-product-${p.id}`} className="bg-[#FFFAF3] border border-[#D6D3CB] p-4 flex items-center gap-4">
                  {p.image_url && <img src={p.image_url} alt={p.name} className="w-14 h-14 object-cover border border-[#D6D3CB]" />}
                  <div className="flex-1">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-1.5 py-0.5 border mr-2 ${p.type === "service" ? "text-[#003366] border-[#003366]/30" : "text-[#7a611c] border-[#CDA434]/50"}`}>
                      {p.type === "service" ? <Wrench size={10} /> : <Package size={10} />} {p.type}
                    </span>
                    <span className="font-serif text-base text-[#1E1E2F]">{p.name}</span>
                    {p.price && <span className="font-sans text-xs text-[#7a611c] ml-2">{p.price}</span>}
                  </div>
                  <button data-testid={`delete-product-${p.id}`} onClick={() => remove(p.id)} className="text-[#505A68] hover:text-[#003366] p-2"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          )}

          {open ? (
            <form onSubmit={add} className="bg-[#FFFAF3] border border-[#D6D3CB] p-6 space-y-4" data-testid="product-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Name</label>
                  <input required data-testid="product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Type</label>
                  <select data-testid="product-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls}>
                    <option value="product">Product</option>
                    <option value="service">Service</option>
                  </select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Price (optional)</label>
                  <input data-testid="product-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="e.g. £48 / case, POA" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Image URL (optional)</label>
                  <input data-testid="product-image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…" className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Description</label>
                <textarea required rows={3} data-testid="product-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} resize-none`} />
              </div>
              <div className="flex gap-2">
                <GoldButton type="submit" disabled={busy} data-testid="product-submit">{busy ? <Loader2 size={16} className="animate-spin" /> : "List it"}</GoldButton>
                <button type="button" onClick={() => setOpen(false)} className="font-sans text-sm text-[#505A68] px-4">Cancel</button>
              </div>
            </form>
          ) : atLimit ? (
            <div className="bg-[#CDA434]/10 border border-[#CDA434]/50 p-5 text-sm font-sans text-[#1B2430]">
              You've reached your plan's limit of {limit}. <a href="#plan" onClick={(e) => { e.preventDefault(); document.getElementById("plan-panel")?.scrollIntoView({ behavior: "smooth" }); }} className="font-semibold text-[#003366] link-underline">Upgrade for more →</a>
            </div>
          ) : (
            <button data-testid="add-product-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 font-sans text-sm font-semibold text-[#003366] border border-[#003366] px-4 py-2.5 hover:bg-[#003366] hover:text-[#FFFAF3] transition-colors">
              <Plus size={15} /> Add a product or service
            </button>
          )}
        </>
      )}
    </div>
  );
}

const inputCls = "w-full bg-[#FFFAF3] border border-[#D6D3CB] focus:border-[#003366] px-4 py-2.5 text-sm font-sans text-[#1B2430]";
const labelCls = "block font-sans text-xs uppercase tracking-wider font-semibold text-[#1B2430] mb-1.5";

const TCATS = [
  { key: "stay", label: "Places to stay" },
  { key: "eat", label: "Places to eat" },
  { key: "see", label: "Places to see" },
  { key: "site", label: "Historically connected site" },
];
const tStatusStyle = {
  live: "text-[#003366] border-[#003366]/30 bg-[#003366]/10",
  pending: "text-[#7a611c] border-[#CDA434]/50 bg-[#CDA434]/15",
  rejected: "text-[#505A68] border-[#D6D3CB]",
};

function TourismManager({ tourism, reload }) {
  const { listings, paid } = tourism;
  const [form, setForm] = useState({ name: "", category: "stay", shore: "lincolnshire", town: "", description: "", image_url: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/tourism", form);
      setForm({ name: "", category: "stay", shore: "lincolnshire", town: "", description: "", image_url: "", website: "" });
      setOpen(false);
      await reload();
      toast.success("Tourism listing submitted for approval.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/my/tourism/${id}`);
    await reload();
    toast.success("Removed.");
  };

  return (
    <div className="mt-12" data-testid="tourism-manager">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-serif text-2xl text-[#1E1E2F] flex items-center gap-2"><MapPin size={20} className="text-[#CDA434]" /> Tourism listings</h2>
        <span className="font-sans text-xs text-[#505A68]">{listings.length} listed</span>
      </div>
      <p className="font-sans text-sm text-[#505A68] mb-5">
        {paid
          ? "As a subscriber, your tourism listings sit at the top of their category all year."
          : "Free listings share five rotating slots per category each month. Subscribe to sit at the top all year."}
      </p>

      {listings.length > 0 && (
        <div className="space-y-3 mb-4">
          {listings.map((t) => (
            <div key={t.id} data-testid={`my-tourism-${t.id}`} className="bg-[#FFFAF3] border border-[#D6D3CB] p-4 flex items-center gap-4">
              {t.image_url && <img src={t.image_url} alt={t.name} className="w-14 h-14 object-cover border border-[#D6D3CB]" />}
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 border ${tStatusStyle[t.status]}`}>{t.status}</span>
                  <span className="text-[10px] uppercase tracking-wide text-[#505A68]">{TCATS.find((c) => c.key === t.category)?.label}</span>
                  {t.paid && <span className="text-[10px] uppercase tracking-wide text-[#7a611c]">Subscribed</span>}
                </div>
                <span className="font-serif text-base text-[#1E1E2F]">{t.name}</span>
              </div>
              <button data-testid={`delete-tourism-${t.id}`} onClick={() => remove(t.id)} className="text-[#505A68] hover:text-[#003366] p-2"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}

      {open ? (
        <form onSubmit={add} className="bg-[#FFFAF3] border border-[#D6D3CB] p-6 space-y-4" data-testid="tourism-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Name</label>
              <input required data-testid="tourism-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <select data-testid="tourism-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                {TCATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Shore</label>
              <select data-testid="tourism-shore" value={form.shore} onChange={(e) => setForm({ ...form, shore: e.target.value })} className={inputCls}>
                <option value="lincolnshire">Lincolnshire</option>
                <option value="massachusetts">Massachusetts</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Town</label>
              <input data-testid="tourism-town" value={form.town} onChange={(e) => setForm({ ...form, town: e.target.value })} className={inputCls} />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Image URL (optional)</label>
              <input data-testid="tourism-image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Website (optional)</label>
              <input data-testid="tourism-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <textarea required rows={2} data-testid="tourism-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} resize-none`} />
          </div>
          <div className="flex gap-2">
            <GoldButton type="submit" disabled={busy} data-testid="tourism-submit">{busy ? <Loader2 size={16} className="animate-spin" /> : "Submit for approval"}</GoldButton>
            <button type="button" onClick={() => setOpen(false)} className="font-sans text-sm text-[#505A68] px-4">Cancel</button>
          </div>
        </form>
      ) : (
        <button data-testid="add-tourism-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 font-sans text-sm font-semibold text-[#003366] border border-[#003366] px-4 py-2.5 hover:bg-[#003366] hover:text-[#FFFAF3] transition-colors">
          <Plus size={15} /> Add a tourism listing
        </button>
      )}
    </div>
  );
}

const statusStyle = {
  live: "bg-[#003366]/10 text-[#003366] border-[#003366]/30",
  pending: "bg-[#CDA434]/15 text-[#7a611c] border-[#CDA434]/50",
  rejected: "bg-[#1E1E2F]/10 text-[#505A68] border-[#D6D3CB]",
};

export default function Account() {
  const { user, ready, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [prod, setProd] = useState({ products: [], limit: 0, plan: "harbour", has_listing: false });
  const [tourism, setTourism] = useState({ listings: [], paid: false, plan: "harbour" });
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState("");

  useEffect(() => {
    if (ready && !user) navigate("/signin");
  }, [ready, user, navigate]);

  const loadProducts = () => api.get("/my/products").then((r) => setProd(r.data));
  const loadTourism = () => api.get("/my/tourism").then((r) => setTourism(r.data));

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get("/my/listing").then((r) => setListing(r.data)),
      api.get("/my/enquiries").then((r) => setEnquiries(r.data)),
      loadProducts(),
      loadTourism(),
    ]).finally(() => setLoading(false));
  }, [user]);

  const upgrade = async (plan) => {
    setUpgrading(plan);
    try {
      await api.post("/account/upgrade", { plan });
      await refreshUser();
      const r = await api.get("/my/listing");
      setListing(r.data);
      await loadProducts();
      await loadTourism();
      toast.success(`Plan set to ${plan}. Checkout is coming next.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUpgrading("");
    }
  };

  if (!ready || !user) {
    return <div className="py-40 flex justify-center"><Loader2 className="animate-spin text-[#CDA434]" size={32} /></div>;
  }

  return (
    <div>
      <section className="bg-[#1E1E2F] text-[#FFFAF3] border-b border-[#D6D3CB]">
        <Container className="py-12">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="font-sans text-xs uppercase tracking-[0.2em] text-[#CDA434] mb-2">Your account</p>
              <h1 className="font-serif text-3xl">{user.name}</h1>
              <p className="font-sans text-sm text-[#FFFAF3]/70 mt-1">{user.email} · {user.role}</p>
            </div>
            <div className="flex items-center gap-3">
              {user.role === "admin" && (
                <OutlineButton as="link" to="/admin" className="border-[#CDA434] text-[#CDA434] hover:bg-[#CDA434] hover:text-[#1E1E2F]">
                  <ShieldCheck size={15} className="mr-2" /> Admin
                </OutlineButton>
              )}
              <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }} className="inline-flex items-center gap-2 font-sans text-sm text-[#FFFAF3]/80 hover:text-[#FFFAF3] border border-[#FFFAF3]/20 px-4 py-2">
                <LogOut size={15} /> Sign out
              </button>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-12 sm:py-16">
        <Container>
          <div className="grid lg:grid-cols-12 gap-8">
            {/* Listing */}
            <div className="lg:col-span-7">
              <h2 className="font-serif text-2xl text-[#1E1E2F] mb-5">Your listing</h2>
              {loading ? (
                <div className="h-40 bg-[#FFFAF3] border border-[#D6D3CB] animate-pulse" />
              ) : listing ? (
                <div data-testid="account-listing" className="bg-[#FFFAF3] border border-[#D6D3CB] overflow-hidden">
                  <div className="flex gap-4 p-6">
                    <img src={listing.image_url || "https://images.pexels.com/photos/8755712/pexels-photo-8755712.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"} alt={listing.name} className="w-28 h-28 object-cover border border-[#D6D3CB] shrink-0" />
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide border ${statusStyle[listing.status]}`} data-testid="listing-status">
                          {listing.status}
                        </span>
                        <PlanBadge plan={listing.plan} />
                        <ShoreBadge shore={listing.shore} />
                      </div>
                      <h3 className="font-serif text-xl text-[#1E1E2F]">{listing.name}</h3>
                      <p className="font-sans text-sm text-[#505A68] mt-1 line-clamp-2">{listing.short_pitch}</p>
                    </div>
                  </div>
                  <div className="px-6 py-4 border-t border-[#D6D3CB] bg-[#F8F1E7] flex items-center justify-between">
                    <p className="font-sans text-xs text-[#505A68]">
                      {listing.status === "pending" && "Awaiting an independent moderator's approval."}
                      {listing.status === "live" && "Live on the Made directory."}
                      {listing.status === "rejected" && "Not approved. Edit and resubmit."}
                    </p>
                    <OutlineButton as="link" to="/account/listing/edit" className="px-4 py-2">
                      <PenLine size={14} className="mr-2" /> Edit
                    </OutlineButton>
                  </div>
                </div>
              ) : (
                <EmptyState
                  testId="account-no-listing"
                  icon={<PenLine size={20} />}
                  heading="No listing yet"
                  text="Submit your business to the crossing. It enters the queue as pending until an admin approves it."
                />
              )}
              {!listing && !loading && (
                <div className="mt-4"><GoldButton as="link" to="/account/listing/edit" data-testid="submit-listing-btn">Submit a listing</GoldButton></div>
              )}

              {/* Products & services */}
              {listing && <ProductManager prod={prod} reload={loadProducts} />}

              {/* Tourism listings */}
              <TourismManager tourism={tourism} reload={loadTourism} />

              {/* Enquiry inbox */}
              <h2 className="font-serif text-2xl text-[#1E1E2F] mt-12 mb-5 flex items-center gap-2"><Inbox size={20} className="text-[#CDA434]" /> Enquiry inbox</h2>
              {enquiries.length === 0 ? (
                <EmptyState testId="account-no-enquiries" heading="No enquiries yet" text="When a visitor sends you a message, it will be laid here on your desk." />
              ) : (
                <div className="space-y-3" data-testid="enquiry-inbox">
                  {enquiries.map((e) => (
                    <div key={e.id} className="bg-[#FFFAF3] border border-[#D6D3CB] p-5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-serif text-base text-[#1E1E2F]">{e.from_name}</span>
                        <span className="font-sans text-xs text-[#505A68]">{new Date(e.created_at).toLocaleDateString("en-GB")}</span>
                      </div>
                      <a href={`mailto:${e.from_email}`} className="font-sans text-xs text-[#003366] link-underline">{e.from_email}</a>
                      <p className="font-sans text-sm text-[#1B2430] leading-relaxed mt-2">{e.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Plan */}
            <div className="lg:col-span-5" id="plan-panel">
              <h2 className="font-serif text-2xl text-[#1E1E2F] mb-5">Your plan</h2>
              <div className="bg-[#FFFAF3] border border-[#D6D3CB] p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-serif text-xl text-[#003366] capitalize">{user.plan || "harbour"}</span>
                  <PlanBadge plan={user.plan} />
                </div>
                <p className="font-sans text-sm text-[#505A68] leading-relaxed mb-5">
                  Change your plan below to preview badges and placement. No card is charged — checkout is coming next.
                </p>
                <div className="space-y-2">
                  {PLANS.map((p) => {
                    const current = (user.plan || "harbour") === p.key;
                    return (
                      <div key={p.key} className={`flex items-center justify-between p-3 border ${current ? "border-[#CDA434] bg-[#CDA434]/10" : "border-[#D6D3CB]"}`}>
                        <div>
                          <span className="font-serif text-base text-[#1E1E2F]">{p.name}</span>
                          <span className="font-sans text-xs text-[#505A68] ml-2">{p.price_month} {p.price_year}</span>
                        </div>
                        {current ? (
                          <span className="font-sans text-xs uppercase tracking-wide text-[#7a611c] font-semibold">Current</span>
                        ) : (
                          <button
                            data-testid={`upgrade-${p.key}`}
                            onClick={() => upgrade(p.key)}
                            disabled={upgrading === p.key}
                            className="font-sans text-xs font-semibold text-[#003366] hover:text-[#CDA434] link-underline"
                          >
                            {upgrading === p.key ? "…" : "Checkout coming next"}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
